import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAudit } from '@/contexts/AuditContext';
import {
  Shield, Loader2, FileBarChart, Users, Plug, AlertCircle,
  Settings2, ChevronDown, Square, CheckSquare, Lock, Plus, Minus,
  User, ArrowRight, Mail, LayoutDashboard, Search, UserPlus,
  CalendarClock, AlertTriangle,
} from 'lucide-react';
import type { PaginationMetadata } from '@/services/api';
import { DiscardChangesDialog } from '@/components/settings/DiscardChangesDialog';
import { TablePagination, DEFAULT_PAGE_SIZE } from '@/components/TablePagination';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSidebar } from '@/components/blocks/evo-sidebar';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  getUserRoleService,
  type UserRolePermissions,
  type CreateUserRoleDto,
  type UpdateUserRoleDto,
  type UserRoleListItem,
  type PagedRoleUser,
} from '@/services/userRoleService';
import { DEFAULT_ROLE_PERMISSIONS_MAP } from '@/services/defaultRolePermissions';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { DataModeToggle } from '@/components/DataModeToggle';

// ── Shared UI helpers ────────────────────────────────────────────────────────

// Same initials fallback as CompanySheet's Users-with-access section.
function getInitials(name: string): string {
  return String(name ?? '')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const PermissionCard: React.FC<{
  label: React.ReactNode;
  description: string;
  control: React.ReactNode;
  dimmed?: boolean;
  onToggle?: () => void;
}> = ({ label, description, control, dimmed, onToggle }) => (
  <div
    className={`flex items-center justify-between gap-4 p-4 rounded-lg border bg-background${dimmed ? ' opacity-60' : ''}${onToggle ? ' cursor-pointer select-none' : ''}`}
    onClick={onToggle}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <div className="shrink-0" onClick={e => e.stopPropagation()}>{control}</div>
  </div>
);

const SubPermissionCard: React.FC<{
  label: string;
  description: string;
  control: React.ReactNode;
  onToggle?: () => void;
}> = ({ label, description, control, onToggle }) => (
  <div
    className={`flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30 ml-4${onToggle ? ' cursor-pointer select-none' : ''}`}
    onClick={onToggle}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <div className="shrink-0" onClick={e => e.stopPropagation()}>{control}</div>
  </div>
);

const SectionAccordionItem: React.FC<{
  value: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ value, icon, title, description, children }) => (
  <AccordionItem value={value} className="border rounded-lg">
    <AccordionTrigger className="px-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="text-left">
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground font-normal">{description}</div>
        </div>
      </div>
    </AccordionTrigger>
    <AccordionContent>
      <div className="px-4 pb-4 space-y-3">
        {children}
      </div>
    </AccordionContent>
  </AccordionItem>
);

// ── Component ────────────────────────────────────────────────────────────────

interface EditRolePageProps {
  roleId?: number | null;
  onNavigate?: (page: string) => void;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'active' | 'inactive';
  photoUrl?: string;
}

export const EditRolePage: React.FC<EditRolePageProps> = ({ roleId, onNavigate }) => {
  const { state: sidebarState } = useSidebar();
  const audit = useAudit();
  const sidebarLeft = sidebarState === 'collapsed' ? '3.5rem' : '13rem';
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersPagination, setUsersPagination] = useState<PaginationMetadata | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newRoleId, setNewRoleId] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [allRoles, setAllRoles] = useState<UserRoleListItem[]>([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState<{ added: string[]; removed: string[] }>({ added: [], removed: [] });

  // Assign user dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState<PagedRoleUser[]>([]);
  const [assignPagination, setAssignPagination] = useState<PaginationMetadata | null>(null);
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [debouncedAssignSearch, setDebouncedAssignSearch] = useState('');
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignRefreshKey, setAssignRefreshKey] = useState(0);
  const [assignPage, setAssignPage] = useState(1);
  const [assignPageSize, setAssignPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Report Access permissions — 6 scheduler reports
  const [enableUtilisationReport, setEnableUtilisationReport] = useState(false);
  const [viewUtilisationReportAccess, setViewUtilisationReportAccess] = useState<string>('own');
  const [allowUtilisationReportExport, setAllowUtilisationReportExport] = useState(false);

  const [enableUtilisationForecastReport, setEnableUtilisationForecastReport] = useState(false);
  const [viewUtilisationForecastReportAccess, setViewUtilisationForecastReportAccess] = useState<string>('own');
  const [allowUtilisationForecastReportExport, setAllowUtilisationForecastReportExport] = useState(false);

  const [enableAvailableCapacityReport, setEnableAvailableCapacityReport] = useState(false);
  const [viewAvailableCapacityReportAccess, setViewAvailableCapacityReportAccess] = useState<string>('own');
  const [allowAvailableCapacityReportExport, setAllowAvailableCapacityReportExport] = useState(false);

  const [enableResourceAllocationReport, setEnableResourceAllocationReport] = useState(false);
  const [viewResourceAllocationReportAccess, setViewResourceAllocationReportAccess] = useState<string>('own');
  const [allowResourceAllocationReportExport, setAllowResourceAllocationReportExport] = useState(false);

  const [enableProjectStaffingReport, setEnableProjectStaffingReport] = useState(false);
  const [viewProjectStaffingReportAccess, setViewProjectStaffingReportAccess] = useState<string>('own');
  const [allowProjectStaffingReportExport, setAllowProjectStaffingReportExport] = useState(false);

  const [enableBenchReport, setEnableBenchReport] = useState(false);
  const [viewBenchReportAccess, setViewBenchReportAccess] = useState<string>('own');
  const [allowBenchReportExport, setAllowBenchReportExport] = useState(false);

  const [enableAuditLogReport, setEnableAuditLogReport] = useState(false);
  const [allowAuditLogReportExport, setAllowAuditLogReportExport] = useState(false);

  // Resource Directory permissions
  const [viewResourceDirectory, setViewResourceDirectory] = useState(false);
  const [viewResourceDetails, setViewResourceDetails] = useState(false);
  const [viewResourceDirectoryAccess, setViewResourceDirectoryAccess] = useState<string>('own');

  // Data & Integration permissions
  const [viewIntegrations, setViewIntegrations] = useState(false);
  const [manageIntegrations, setManageIntegrations] = useState(false);

  // Settings permissions
  const [viewGeneralSettings, setViewGeneralSettings] = useState(false);
  const [viewTimezoneSettings, setViewTimezoneSettings] = useState(false);
  const [viewBookingTypeSetup, setViewBookingTypeSetup] = useState(false);
  const [viewGroupSetup, setViewGroupSetup] = useState(false);
  const [viewRolesPermissions, setViewRolesPermissions] = useState(false);
  const [viewUserSetup, setViewUserSetup] = useState(false);

  // Navigation permissions
  const [viewDashboard, setViewDashboard] = useState(false);
  const [viewHeatmap, setViewHeatmap] = useState(false);
  const [viewOpenDemand, setViewOpenDemand] = useState(false);
  const [viewProjects, setViewProjects] = useState(false);

  // Booking permissions
  const [createBooking, setCreateBooking] = useState(true);
  const [editBooking, setEditBooking] = useState(true);
  const [deleteBooking, setDeleteBooking] = useState(true);
  const [overrideReadOnly, setOverrideReadOnly] = useState(true);

  const isCreateMode = !roleId;
  const { getScreenMode } = useDataMode();
  const { currentCompany } = useCompany();
  const { refreshPermissions } = usePermissions();
  const currentMode = getScreenMode('settings');
  const userRoleService = useMemo(() => getUserRoleService(currentMode), [currentMode]);

  const isAdministratorRole = useMemo(
    () => !isCreateMode && isSystem && roleName.trim().toLowerCase() === 'administrator',
    [isCreateMode, isSystem, roleName],
  );

  useEffect(() => {
    if (isAdministratorRole) setViewRolesPermissions(true);
  }, [isAdministratorRole]);

  // Permission snapshot for diff computation
  type PermissionSnapshot = Record<string, boolean | string>;

  const PERMISSION_LABELS: Record<string, string> = {
    enableUtilisationReport: 'Utilisation Report',
    viewUtilisationReportAccess: 'Utilisation Report View Access',
    allowUtilisationReportExport: 'Utilisation Report Export',
    enableUtilisationForecastReport: 'Utilisation Forecast Report',
    viewUtilisationForecastReportAccess: 'Utilisation Forecast View Access',
    allowUtilisationForecastReportExport: 'Utilisation Forecast Export',
    enableAvailableCapacityReport: 'Available Capacity Report',
    viewAvailableCapacityReportAccess: 'Available Capacity View Access',
    allowAvailableCapacityReportExport: 'Available Capacity Export',
    enableResourceAllocationReport: 'Resource Allocation Report',
    viewResourceAllocationReportAccess: 'Resource Allocation View Access',
    allowResourceAllocationReportExport: 'Resource Allocation Export',
    enableProjectStaffingReport: 'Project Staffing Report',
    viewProjectStaffingReportAccess: 'Project Staffing View Access',
    allowProjectStaffingReportExport: 'Project Staffing Export',
    enableBenchReport: 'Bench Report',
    viewBenchReportAccess: 'Bench Report View Access',
    allowBenchReportExport: 'Bench Report Export',
    enableAuditLogReport: 'Audit Log Report',
    allowAuditLogReportExport: 'Audit Log Report Export',
    viewResourceDirectory: 'View Resource Directory',
    viewResourceDetails: 'View Resource Details',
    viewResourceDirectoryAccess: 'View Resources Access',
    viewIntegrations: 'View Integrations',
    manageIntegrations: 'Manage Integrations',
    viewGeneralSettings: 'View General Settings',
    viewTimezoneSettings: 'View Timezone Settings',
    viewBookingTypeSetup: 'View Booking Type Setup',
    viewGroupSetup: 'View Planner Group Setup',
    viewRolesPermissions: 'View Roles & Permissions',
    viewUserSetup: 'View User Setup',
    viewDashboard: 'View Dashboard',
    viewHeatmap: 'View Heatmap',
    viewOpenDemand: 'View Open Demand',
    viewProjects: 'View Projects',
    createBooking: 'Create Bookings',
    editBooking: 'Edit Bookings',
    deleteBooking: 'Delete Bookings',
    overrideReadOnly: 'Override Read-Only Bookings',
  };

  const savedSnapshot = useRef<PermissionSnapshot>({});

  const getCurrentSnapshot = useCallback((): PermissionSnapshot => ({
    enableUtilisationReport,
    viewUtilisationReportAccess,
    allowUtilisationReportExport,
    enableUtilisationForecastReport,
    viewUtilisationForecastReportAccess,
    allowUtilisationForecastReportExport,
    enableAvailableCapacityReport,
    viewAvailableCapacityReportAccess,
    allowAvailableCapacityReportExport,
    enableResourceAllocationReport,
    viewResourceAllocationReportAccess,
    allowResourceAllocationReportExport,
    enableProjectStaffingReport,
    viewProjectStaffingReportAccess,
    allowProjectStaffingReportExport,
    enableBenchReport,
    viewBenchReportAccess,
    allowBenchReportExport,
    enableAuditLogReport,
    allowAuditLogReportExport,
    viewResourceDirectory,
    viewResourceDetails,
    viewResourceDirectoryAccess,
    viewIntegrations,
    manageIntegrations,
    viewGeneralSettings,
    viewTimezoneSettings,
    viewBookingTypeSetup,
    viewGroupSetup,
    viewRolesPermissions,
    viewUserSetup,
    viewDashboard,
    viewHeatmap,
    viewOpenDemand,
    viewProjects,
    createBooking,
    editBooking,
    deleteBooking,
    overrideReadOnly,
  }), [
    enableUtilisationReport, viewUtilisationReportAccess, allowUtilisationReportExport,
    enableUtilisationForecastReport, viewUtilisationForecastReportAccess, allowUtilisationForecastReportExport,
    enableAvailableCapacityReport, viewAvailableCapacityReportAccess, allowAvailableCapacityReportExport,
    enableResourceAllocationReport, viewResourceAllocationReportAccess, allowResourceAllocationReportExport,
    enableProjectStaffingReport, viewProjectStaffingReportAccess, allowProjectStaffingReportExport,
    enableBenchReport, viewBenchReportAccess, allowBenchReportExport,
    enableAuditLogReport, allowAuditLogReportExport,
    viewResourceDirectory, viewResourceDetails, viewResourceDirectoryAccess,
    viewIntegrations, manageIntegrations,
    viewGeneralSettings, viewTimezoneSettings, viewBookingTypeSetup, viewGroupSetup, viewRolesPermissions, viewUserSetup,
    viewDashboard, viewHeatmap, viewOpenDemand, viewProjects,
    createBooking, editBooking, deleteBooking, overrideReadOnly,
  ]);

  // Keys excluded from audit logging — "view" navigation/settings permissions
  const AUDIT_EXCLUDED_KEYS = useMemo(() => new Set([
    'viewResourceDirectory', 'viewResourceDetails',
    'viewIntegrations',
    'viewGeneralSettings', 'viewTimezoneSettings', 'viewBookingTypeSetup',
    'viewGroupSetup', 'viewRolesPermissions', 'viewUserSetup',
    'viewDashboard', 'viewHeatmap', 'viewOpenDemand', 'viewProjects',
    'manageIntegrations',
  ]), []);

  const computePermissionDiff = useCallback(() => {
    const current = getCurrentSnapshot();
    const added: string[] = [];
    const removed: string[] = [];
    const changed: { field: string; before: string; after: string }[] = [];
    for (const key of Object.keys(current)) {
      if (AUDIT_EXCLUDED_KEYS.has(key)) continue;
      const prev = savedSnapshot.current[key];
      const cur = current[key];
      if (prev !== cur) {
        const label = PERMISSION_LABELS[key] || key;
        if (typeof cur === 'boolean') {
          if (cur) added.push(label);
          else removed.push(label);
        } else {
          changed.push({ field: label, before: String(prev ?? ''), after: String(cur) });
        }
      }
    }
    return { added, removed, changed };
  }, [getCurrentSnapshot, AUDIT_EXCLUDED_KEYS]);

  // ── Permission counts ──────────────────────────────────────────────────────

  const tabPermissions: Record<string, boolean[]> = {
    reports: [
      enableUtilisationReport,
      enableUtilisationForecastReport,
      enableAvailableCapacityReport,
      enableResourceAllocationReport,
      enableProjectStaffingReport,
      enableBenchReport,
      enableAuditLogReport,
    ],
    management: [
      viewResourceDirectory,
      viewResourceDetails,
      viewGeneralSettings,
      viewTimezoneSettings,
      viewBookingTypeSetup,
      viewGroupSetup,
      viewRolesPermissions,
      viewUserSetup,
      viewDashboard,
      viewHeatmap,
      viewOpenDemand,
      viewProjects,
      createBooking,
      editBooking,
      deleteBooking,
      overrideReadOnly,
    ],
    data: [viewIntegrations, manageIntegrations],
  };

  const getTabPermissionCount = (tab: string): number =>
    (tabPermissions[tab] ?? []).filter(Boolean).length;

  const getTabPermissionTotal = (tab: string): number => (tabPermissions[tab] ?? []).length;

  const getTotalPermissionsCount = (): number => {
    return getTabPermissionCount('reports') + getTabPermissionCount('management') + getTabPermissionCount('data');
  };

  // ── Bulk actions ───────────────────────────────────────────────────────────

  const handleSelectAllReports = () => {
    setEnableUtilisationReport(true);
    setEnableUtilisationForecastReport(true);
    setEnableAvailableCapacityReport(true);
    setEnableResourceAllocationReport(true);
    setEnableProjectStaffingReport(true);
    setEnableBenchReport(true);
    setEnableAuditLogReport(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllReports = () => {
    setEnableUtilisationReport(false); setAllowUtilisationReportExport(false);
    setEnableUtilisationForecastReport(false); setAllowUtilisationForecastReportExport(false);
    setEnableAvailableCapacityReport(false); setAllowAvailableCapacityReportExport(false);
    setEnableResourceAllocationReport(false); setAllowResourceAllocationReportExport(false);
    setEnableProjectStaffingReport(false); setAllowProjectStaffingReportExport(false);
    setEnableBenchReport(false); setAllowBenchReportExport(false);
    setEnableAuditLogReport(false); setAllowAuditLogReportExport(false);
    setHasUnsavedChanges(true);
  };

  const handleSelectAllManagement = () => {
    setViewResourceDirectory(true);
    setViewResourceDetails(true);
    setViewGeneralSettings(true);
    setViewTimezoneSettings(true);
    setViewBookingTypeSetup(true);
    setViewGroupSetup(true);
    setViewRolesPermissions(true);
    setViewUserSetup(true);
    setViewDashboard(true);
    setViewHeatmap(true);
    setViewOpenDemand(true);
    setViewProjects(true);
    setCreateBooking(true);
    setEditBooking(true);
    setDeleteBooking(true);
    setOverrideReadOnly(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllManagement = () => {
    setViewResourceDirectory(false);
    setViewResourceDetails(false);
    setViewGeneralSettings(false);
    setViewTimezoneSettings(false);
    setViewBookingTypeSetup(false);
    setViewGroupSetup(false);
    setViewRolesPermissions(isAdministratorRole);
    setViewUserSetup(false);
    setViewDashboard(false);
    setViewHeatmap(false);
    setViewOpenDemand(false);
    setViewProjects(false);
    setCreateBooking(false);
    setEditBooking(false);
    setDeleteBooking(false);
    setOverrideReadOnly(false);
    setHasUnsavedChanges(true);
  };

  const handleSelectAllDataIntegration = () => {
    setViewIntegrations(true);
    setManageIntegrations(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllDataIntegration = () => {
    setViewIntegrations(false);
    setManageIntegrations(false);
    setHasUnsavedChanges(true);
  };

  // ── Load / Save ────────────────────────────────────────────────────────────

  const loadPermissionsFromData = (permissionsData: UserRolePermissions, isInitialLoad = false) => {
    try {
      if (permissionsData.reportAccessPermissions) {
        const r = permissionsData.reportAccessPermissions;
        setEnableUtilisationReport(r.utilisationReport?.enabled || false);
        setViewUtilisationReportAccess(r.utilisationReport?.viewAccess || 'own');
        setAllowUtilisationReportExport(r.utilisationReport?.allowExport || false);
        setEnableUtilisationForecastReport(r.utilisationForecastReport?.enabled || false);
        setViewUtilisationForecastReportAccess(r.utilisationForecastReport?.viewAccess || 'own');
        setAllowUtilisationForecastReportExport(r.utilisationForecastReport?.allowExport || false);
        setEnableAvailableCapacityReport(r.availableCapacityReport?.enabled || false);
        setViewAvailableCapacityReportAccess(r.availableCapacityReport?.viewAccess || 'own');
        setAllowAvailableCapacityReportExport(r.availableCapacityReport?.allowExport || false);
        setEnableResourceAllocationReport(r.resourceAllocationReport?.enabled || false);
        setViewResourceAllocationReportAccess(r.resourceAllocationReport?.viewAccess || 'own');
        setAllowResourceAllocationReportExport(r.resourceAllocationReport?.allowExport || false);
        setEnableProjectStaffingReport(r.projectStaffingReport?.enabled || false);
        setViewProjectStaffingReportAccess(r.projectStaffingReport?.viewAccess || 'own');
        setAllowProjectStaffingReportExport(r.projectStaffingReport?.allowExport || false);
        setEnableBenchReport(r.benchReport?.enabled || false);
        setViewBenchReportAccess(r.benchReport?.viewAccess || 'own');
        setAllowBenchReportExport(r.benchReport?.allowExport || false);
        setEnableAuditLogReport(r.auditLogReport?.enabled || false);
        setAllowAuditLogReportExport(r.auditLogReport?.allowExport || false);
      }
      if (permissionsData.resourceDirectoryPermissions) {
        setViewResourceDirectory(permissionsData.resourceDirectoryPermissions.viewDirectory || false);
        setViewResourceDetails(permissionsData.resourceDirectoryPermissions.viewDetails || false);
        setViewResourceDirectoryAccess(permissionsData.resourceDirectoryPermissions.viewAccess || 'own');
      }
      if (permissionsData.integrationsPermissions) {
        const manage = permissionsData.integrationsPermissions.manageIntegrations || false;
        setManageIntegrations(manage);
        // Enforce cascade: Manage implies View.
        setViewIntegrations(manage || permissionsData.integrationsPermissions.viewIntegrations || false);
      }
      if (permissionsData.settingsPermissions) {
        setViewGeneralSettings(permissionsData.settingsPermissions.viewGeneral || false);
        setViewTimezoneSettings(permissionsData.settingsPermissions.viewTimezone || false);
        setViewBookingTypeSetup(permissionsData.settingsPermissions.viewBookingTypeSetup || false);
        setViewGroupSetup(permissionsData.settingsPermissions.viewGroupSetup || false);
        setViewRolesPermissions(permissionsData.settingsPermissions.viewRolesPermissions || false);
        setViewUserSetup(permissionsData.settingsPermissions.viewUserSetup || false);
      }
      if (permissionsData.navigationPermissions) {
        setViewDashboard(permissionsData.navigationPermissions.viewDashboard || false);
        setViewHeatmap(permissionsData.navigationPermissions.viewHeatmap || false);
        setViewOpenDemand(permissionsData.navigationPermissions.viewOpenDemand || false);
        setViewProjects(permissionsData.navigationPermissions.viewProjects || false);
      }
      // Booking permissions default to allowed when the key (or the whole
      // object) is absent — e.g. roles created before booking permissions
      // existed. Always set the toggles so a missing object doesn't leave stale
      // state and the editor matches what /me/permissions reports.
      setCreateBooking(permissionsData.bookingPermissions?.createBooking ?? true);
      setEditBooking(permissionsData.bookingPermissions?.editBooking ?? true);
      setDeleteBooking(permissionsData.bookingPermissions?.deleteBooking ?? true);
      setOverrideReadOnly(permissionsData.bookingPermissions?.overrideReadOnly ?? true);

      if (isInitialLoad) {
        setTimeout(() => {
          savedSnapshot.current = getCurrentSnapshot();
        }, 0);
      }
    } catch (error) {
      console.error('Error loading permissions from data:', error);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
      return;
    }
    onNavigate?.('roles-permissions');
  };

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false);
    setHasUnsavedChanges(false);
    onNavigate?.('roles-permissions');
  };

  useEffect(() => {
    const loadRoleData = async () => {
      if (isCreateMode || !roleId) return;
      try {
        // The role carries the employees assigned to it, so its users are read
        // from this response rather than asked for separately.
        const role = await userRoleService.getUserRoleById(roleId);
        setRoleName(role.name);
        setRoleDescription(role.description || '');
        setIsSystem(role.isSystem);
        setIsActive(role.isSystem ? true : role.isActive);
        if (role.permissions) loadPermissionsFromData(role.permissions, true);

      } catch (error) {
        console.error('Error loading role:', error);
        toast.error('Failed to load role data');
      }
    };
    loadRoleData();
  }, [roleId, isCreateMode, userRoleService, currentCompany?.id]);

  // Debounce the Users-tab search ~300ms, matching the CompanySheet reference.
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedUserSearch(userSearchQuery.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [userSearchQuery]);

  // Server-paged fetch for the Users tab — drives off page/pageSize/search.
  useEffect(() => {
    if (isCreateMode || !roleId) return;
    const controller = new AbortController();
    setIsLoadingUsers(true);
    userRoleService.getRoleEmployeesPaged(roleId, userPage, userPageSize, debouncedUserSearch, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setUsers(response.items.map(u => ({
          id: String(u.id),
          name: u.name,
          email: u.email,
          roleId: String(roleId),
          roleName: u.role || roleName,
          status: u.status === 'active' ? 'active' : 'inactive',
          photoUrl: u.photoUrl,
        })));
        setUsersPagination(response.pagination);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load role users', roleId, error);
        toast.error('Failed to load users');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingUsers(false);
      });
    return () => controller.abort();
  }, [roleId, isCreateMode, userPage, userPageSize, debouncedUserSearch, usersRefreshKey, userRoleService, currentCompany?.id]);

  // Debounce + fetch for the Assign dialog's server-paged resource list.
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedAssignSearch(assignSearchQuery.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [assignSearchQuery]);

  useEffect(() => {
    if (!assignDialogOpen || !roleId) return;
    const controller = new AbortController();
    setIsLoadingResources(true);
    userRoleService.getAssignableResourcesPaged(roleId, assignPage, assignPageSize, debouncedAssignSearch, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setAvailableResources(response.items);
        setAssignPagination(response.pagination);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load assignable resources', roleId, error);
        toast.error('Failed to load resources');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingResources(false);
      });
    return () => controller.abort();
  }, [assignDialogOpen, roleId, assignPage, assignPageSize, debouncedAssignSearch, assignRefreshKey, userRoleService]);

  /**
   * The other roles a user can be transferred to. Only the transfer dialog uses
   * them, so the role list is read when it is first opened rather than on every
   * visit to this page.
   */
  useEffect(() => {
    if (!transferDialogOpen || allRoles.length > 0) return;
    let cancelled = false;
    userRoleService.getAllUserRoles()
      .then(roles => { if (!cancelled) setAllRoles(roles); })
      .catch(error => console.error('Error loading all roles:', error));
    return () => { cancelled = true; };
  }, [transferDialogOpen, allRoles.length, userRoleService]);

  // Save snapshot after initial load settles
  useEffect(() => {
    if (!isCreateMode && roleId) {
      savedSnapshot.current = getCurrentSnapshot();
    }
  }, []);

  const performSave = async () => {
    setIsSaving(true);
    try {
      const permissionsPayload: UserRolePermissions = {
        reportAccessPermissions: {
          utilisationReport:         { enabled: enableUtilisationReport, viewAccess: viewUtilisationReportAccess, allowExport: allowUtilisationReportExport },
          utilisationForecastReport: { enabled: enableUtilisationForecastReport, viewAccess: viewUtilisationForecastReportAccess, allowExport: allowUtilisationForecastReportExport },
          availableCapacityReport:   { enabled: enableAvailableCapacityReport, viewAccess: viewAvailableCapacityReportAccess, allowExport: allowAvailableCapacityReportExport },
          resourceAllocationReport:  { enabled: enableResourceAllocationReport, viewAccess: viewResourceAllocationReportAccess, allowExport: allowResourceAllocationReportExport },
          projectStaffingReport:     { enabled: enableProjectStaffingReport, viewAccess: viewProjectStaffingReportAccess, allowExport: allowProjectStaffingReportExport },
          benchReport:               { enabled: enableBenchReport, viewAccess: viewBenchReportAccess, allowExport: allowBenchReportExport },
          auditLogReport:            { enabled: enableAuditLogReport, viewAccess: 'all', allowExport: allowAuditLogReportExport },
        },
        resourceDirectoryPermissions: { viewDirectory: viewResourceDirectory, viewDetails: viewResourceDetails, viewAccess: viewResourceDirectoryAccess },
        integrationsPermissions: { viewIntegrations: viewIntegrations || manageIntegrations, manageIntegrations },
        companyManagementPermissions: { viewCompanies: viewIntegrations || manageIntegrations, manageCompanies: manageIntegrations },
        settingsPermissions: {
          viewGeneral: viewGeneralSettings,
          viewTimezone: viewTimezoneSettings,
          viewBookingTypeSetup,
          viewGroupSetup,
          viewRolesPermissions: isAdministratorRole || viewRolesPermissions,
          viewUserSetup,
        },
        navigationPermissions: {
          viewDashboard,
          viewHeatmap,
          viewOpenDemand,
          viewProjects,
        },
        bookingPermissions: {
          createBooking,
          editBooking,
          deleteBooking,
          overrideReadOnly,
        },
      };

      if (isCreateMode) {
        const createDto: CreateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.createUserRole(createDto);
      } else {
        const updateDto: UpdateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.updateUserRole(roleId!, updateDto);
      }

      if (!isCreateMode) {
        await refreshPermissions();
      }

      // Audit log
      const diff = computePermissionDiff();
      const changes = [
        ...diff.added.map((p) => ({ field: p, before: 'Disabled', after: 'Enabled' })),
        ...diff.removed.map((p) => ({ field: p, before: 'Enabled', after: 'Disabled' })),
        ...diff.changed.map((c) => ({ field: c.field, before: c.before, after: c.after })),
      ];
      audit.log({
        user: 'Current User',
        category: 'Roles & Permissions',
        action: isCreateMode ? 'New' : 'Edit',
        entity: roleName.trim(),
        changes,
      });

      setIsSaving(false);
      setHasUnsavedChanges(false);
      setShowSaveConfirmation(false);

      toast.success(isCreateMode ? 'Role created successfully' : 'Role updated successfully', {
        description: `${roleName} has been ${isCreateMode ? 'created' : 'updated'} with ${getTotalPermissionsCount()} permissions.`,
      });

      onNavigate?.('roles-permissions');
    } catch (error: unknown) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
        setNameError(`A role with the name '${roleName.trim()}' already exists`);
        setActiveTab('details');
        toast.error('Duplicate role name', { description: `A role with the name '${roleName.trim()}' already exists. Please choose a different name.` });
      } else {
        toast.error('Save failed', { description: errorMessage || 'Failed to save role' });
      }
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!roleName.trim()) { setNameError('Role name is required'); setActiveTab('details'); return; }
    if (roleName.trim().length < 2) { setNameError('Role name must be at least 2 characters'); setActiveTab('details'); return; }
    setNameError('');
    if (!isCreateMode) {
      setPermissionChanges(computePermissionDiff());
      setShowSaveConfirmation(true);
    } else {
      await performSave();
    }
  };

  const handleApplyDefaultTemplate = useCallback(() => {
    if (!roleId) return;
    const defaults = DEFAULT_ROLE_PERMISSIONS_MAP[roleId];
    if (!defaults) return;
    loadPermissionsFromData(defaults);
    setHasUnsavedChanges(true);
    toast.info('Default template applied', { description: 'Permissions have been reset to defaults for this role.' });
  }, [roleId]);

  const handleConfirmTransfer = async () => {
    if (!selectedUser || !newRoleId || !roleId) return;
    try {
      await userRoleService.transferEmployeeToRole(roleId, Number(newRoleId), Number(selectedUser.id));
      setUsersRefreshKey(k => k + 1);
      toast.success('User transferred', { description: `${selectedUser.name} has been transferred to the new role.` });
      window.dispatchEvent(new Event('rolesUpdated'));
      setTransferDialogOpen(false);
      setSelectedUser(null);
      setNewRoleId('');
    } catch (error: unknown) {
      toast.error('Transfer failed', { description: error instanceof Error ? error.message : 'Failed to transfer user' });
    }
  };

  const handleOpenAssignDialog = () => {
    setAssignDialogOpen(true);
    setAssignSearchQuery('');
    setDebouncedAssignSearch('');
    setAssignPage(1);
    setAvailableResources([]);
    setAssignPagination(null);
  };

  const handleAssignUser = async (resource: PagedRoleUser) => {
    if (!roleId) return;
    setIsAssigning(true);
    try {
      await userRoleService.assignEmployeeToRole(roleId, Number(resource.id));
      // Refetch both lists from the server so the dialog excludes the new member
      // and the Users-tab count stays correct.
      setAssignRefreshKey(k => k + 1);
      setUsersRefreshKey(k => k + 1);
      toast.success('User assigned', { description: `${resource.name} has been assigned to ${roleName}.` });
      window.dispatchEvent(new Event('rolesUpdated'));
    } catch (error: unknown) {
      toast.error('Assignment failed', { description: error instanceof Error ? error.message : 'Failed to assign user' });
    } finally {
      setIsAssigning(false);
    }
  };

  // ── Bulk actions dropdown helper ──────────────────────────────────────────

  const BulkActionsDropdown: React.FC<{ onSelectAll: () => void; onDeselectAll: () => void }> = ({ onSelectAll, onDeselectAll }) => (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-2">
            <Settings2 className="h-4 w-4" />
            Bulk Actions
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={onSelectAll}>
            <CheckSquare className="mr-2 h-4 w-4" />
            Select All
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDeselectAll}>
            <Square className="mr-2 h-4 w-4" />
            Deselect All
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const usersTotal = usersPagination?.totalItems ?? users.length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6 pb-24 overflow-y-auto h-full">

      {/* Page Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">
              {isCreateMode ? 'Create New Role' : (roleName || 'Edit Role')}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isCreateMode ? 'Set up a new role with specific permissions and access levels' : 'Configure role details and permissions'}
            </p>
          </div>
        </div>
        <DataModeToggle screen="settings" />
      </div>

      {/* Unsaved Changes Alert */}
      {hasUnsavedChanges && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Click &quot;{isCreateMode ? 'Create Role' : 'Save Settings'}&quot; at the bottom to apply your changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Platform role info */}
      {isSystem && (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Platform Role</span> — You can modify permissions but cannot delete this role.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div
          className="overflow-x-auto overscroll-x-contain"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <TabsList className="flex w-max min-w-full flex-nowrap justify-start lg:grid lg:w-full lg:grid-cols-5">
            <TabsTrigger value="details" className="gap-2">
              <User className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <FileBarChart className="h-4 w-4" />
              Reports & Analytics
              {getTabPermissionCount('reports') > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('reports')}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="management" className="gap-2">
              <Users className="h-4 w-4" />
              Management
              {getTabPermissionCount('management') > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('management')}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2">
              <Plug className="h-4 w-4" />
              Data & Integration
              {getTabPermissionCount('data') > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('data')}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{usersTotal}</Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ── Details Tab ─────────────────────────────────────────────────────── */}
        <TabsContent value="details" className="space-y-4">
          <Accordion type="multiple" defaultValue={['role-details', 'role-summary']} className="space-y-3">

            {/* Role Details */}
            <SectionAccordionItem value="role-details" icon={<User className="h-5 w-5" />} title="Role Details" description="Configure the basic information for this role">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                {/* Role Name */}
                <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background">
                  <Label htmlFor="role-name" className="text-sm font-medium">
                    Role Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="role-name"
                    value={roleName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRoleName(v);
                      setHasUnsavedChanges(true);
                      if (!v.trim()) setNameError('Role name is required');
                      else if (v.trim().length < 2) setNameError('Role name must be at least 2 characters');
                      else setNameError('');
                    }}
                    placeholder="e.g., Scheduler Manager"
                    disabled={isSystem}
                    className={nameError ? 'border-destructive' : ''}
                  />
                  {nameError
                    ? <p className="text-xs text-destructive">{nameError}</p>
                    : <p className="text-xs text-muted-foreground">{isSystem ? 'Platform role names cannot be changed' : 'The display name for this role'}</p>
                  }
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="role-active" className="text-sm font-medium">Active</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Enable this role to make it available for assignment to users</p>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="shrink-0">
                          <Checkbox
                            id="role-active"
                            checked={isActive}
                            onCheckedChange={(checked) => { setIsActive(!!checked); setHasUnsavedChanges(true); }}
                            disabled={isSystem}
                          />
                        </span>
                      </TooltipTrigger>
                      {isSystem && (
                        <TooltipContent side="top" align="end" className="max-w-[220px] text-center">
                          <p>Platform roles must remain active as they are required for system functionality</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Description — spans full width */}
                <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background lg:col-span-2">
                  <Label htmlFor="role-description" className="text-sm font-medium">Description</Label>
                  <Textarea
                    id="role-description"
                    value={roleDescription}
                    onChange={(e) => { setRoleDescription(e.target.value); setHasUnsavedChanges(true); }}
                    placeholder="Describe what this role can do..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">A brief description of what this role can do</p>
                </div>
              </div>
            </SectionAccordionItem>

            {/* Summary */}
            <SectionAccordionItem value="role-summary" icon={<Shield className="h-5 w-5" />} title="Summary" description="Overview of enabled permissions across all categories">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                <button
                  onClick={() => setActiveTab('users')}
                  className="rounded-lg border bg-background p-4 text-left hover:bg-accent hover:border-primary transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-bold">{usersTotal}</p>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Users assigned to this role</p>
                  <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view users →</p>
                </button>
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-3xl font-bold">{getTotalPermissionsCount()}</p>
                  <p className="text-sm text-muted-foreground mt-1">Total permissions enabled</p>
                </div>
                <div className="rounded-lg border bg-background p-4">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${isActive ? 'bg-primary' : 'bg-muted-foreground'}`} />
                    <p className="text-3xl font-bold">{isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Role status</p>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-background space-y-2">
                <p className="text-sm font-medium mb-2">Permissions by Category:</p>
                <div className="flex items-center justify-between text-sm py-1.5 border-b">
                  <div className="flex items-center gap-2">
                    <FileBarChart className="h-4 w-4" />
                    <span>Reports & Analytics</span>
                  </div>
                  <span className="text-muted-foreground">{getTabPermissionCount('reports')}/{getTabPermissionTotal('reports')}</span>
                </div>
                <div className="flex items-center justify-between text-sm py-1.5 border-b">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>Management</span>
                  </div>
                  <span className="text-muted-foreground">{getTabPermissionCount('management')}/{getTabPermissionTotal('management')}</span>
                </div>
                <div className="flex items-center justify-between text-sm py-1.5">
                  <div className="flex items-center gap-2">
                    <Plug className="h-4 w-4" />
                    <span>Data & Integration</span>
                  </div>
                  <span className="text-muted-foreground">{getTabPermissionCount('data')}/{getTabPermissionTotal('data')}</span>
                </div>
              </div>

              {/* Apply Default Template button for system roles */}
              {isSystem && DEFAULT_ROLE_PERMISSIONS_MAP[roleId!] && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" onClick={handleApplyDefaultTemplate}>
                    Apply Default Template
                  </Button>
                </div>
              )}
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Reports & Analytics Tab ─────────────────────────────────────────── */}
        <TabsContent value="reports" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllReports} onDeselectAll={handleDeselectAllReports} />
          <Accordion type="multiple" defaultValue={['report-access']} className="space-y-3">

            <SectionAccordionItem value="report-access" icon={<FileBarChart className="h-5 w-5" />} title="Report Access" description="Control access to various reports and analytics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                {/* Utilisation Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-utilisation-report" className="cursor-pointer">Utilisation Report</Label>}
                    description="Access to utilisation reports showing resource usage and allocation efficiency"
                    onToggle={() => { setEnableUtilisationReport(v => { if (v) setAllowUtilisationReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-utilisation-report"
                        checked={enableUtilisationReport}
                        onCheckedChange={(c) => { setEnableUtilisationReport(c as boolean); if (!c) setAllowUtilisationReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableUtilisationReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which utilisation data users can view in reports"
                        control={
                          <Select value={viewUtilisationReportAccess} onValueChange={(v) => { setViewUtilisationReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting utilisation report data to Excel or CSV"
                        onToggle={() => { setAllowUtilisationReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowUtilisationReportExport} onCheckedChange={(c) => { setAllowUtilisationReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Utilisation Forecast Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-utilisation-forecast-report" className="cursor-pointer">Utilisation Forecast</Label>}
                    description="Access to forward-looking utilisation forecast reports and projections"
                    onToggle={() => { setEnableUtilisationForecastReport(v => { if (v) setAllowUtilisationForecastReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-utilisation-forecast-report"
                        checked={enableUtilisationForecastReport}
                        onCheckedChange={(c) => { setEnableUtilisationForecastReport(c as boolean); if (!c) setAllowUtilisationForecastReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableUtilisationForecastReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which forecast data users can view"
                        control={
                          <Select value={viewUtilisationForecastReportAccess} onValueChange={(v) => { setViewUtilisationForecastReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting forecast report data to Excel or CSV"
                        onToggle={() => { setAllowUtilisationForecastReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowUtilisationForecastReportExport} onCheckedChange={(c) => { setAllowUtilisationForecastReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Available Capacity Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-available-capacity-report" className="cursor-pointer">Available Capacity</Label>}
                    description="Access to reports showing available resource capacity and bandwidth"
                    onToggle={() => { setEnableAvailableCapacityReport(v => { if (v) setAllowAvailableCapacityReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-available-capacity-report"
                        checked={enableAvailableCapacityReport}
                        onCheckedChange={(c) => { setEnableAvailableCapacityReport(c as boolean); if (!c) setAllowAvailableCapacityReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableAvailableCapacityReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which capacity data users can view"
                        control={
                          <Select value={viewAvailableCapacityReportAccess} onValueChange={(v) => { setViewAvailableCapacityReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting capacity report data to Excel or CSV"
                        onToggle={() => { setAllowAvailableCapacityReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowAvailableCapacityReportExport} onCheckedChange={(c) => { setAllowAvailableCapacityReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Resource Allocation Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-resource-allocation-report" className="cursor-pointer">Resource Allocation</Label>}
                    description="Access to reports showing how resources are allocated across projects"
                    onToggle={() => { setEnableResourceAllocationReport(v => { if (v) setAllowResourceAllocationReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-resource-allocation-report"
                        checked={enableResourceAllocationReport}
                        onCheckedChange={(c) => { setEnableResourceAllocationReport(c as boolean); if (!c) setAllowResourceAllocationReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableResourceAllocationReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which allocation data users can view"
                        control={
                          <Select value={viewResourceAllocationReportAccess} onValueChange={(v) => { setViewResourceAllocationReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting allocation report data to Excel or CSV"
                        onToggle={() => { setAllowResourceAllocationReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowResourceAllocationReportExport} onCheckedChange={(c) => { setAllowResourceAllocationReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Project Staffing Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-project-staffing-report" className="cursor-pointer">Project Staffing</Label>}
                    description="Access to reports showing project staffing levels and requirements"
                    onToggle={() => { setEnableProjectStaffingReport(v => { if (v) setAllowProjectStaffingReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-project-staffing-report"
                        checked={enableProjectStaffingReport}
                        onCheckedChange={(c) => { setEnableProjectStaffingReport(c as boolean); if (!c) setAllowProjectStaffingReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableProjectStaffingReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which staffing data users can view"
                        control={
                          <Select value={viewProjectStaffingReportAccess} onValueChange={(v) => { setViewProjectStaffingReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting staffing report data to Excel or CSV"
                        onToggle={() => { setAllowProjectStaffingReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowProjectStaffingReportExport} onCheckedChange={(c) => { setAllowProjectStaffingReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Bench Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-bench-report" className="cursor-pointer">Bench Report</Label>}
                    description="Access to bench reports showing unallocated resources and availability"
                    onToggle={() => { setEnableBenchReport(v => { if (v) setAllowBenchReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-bench-report"
                        checked={enableBenchReport}
                        onCheckedChange={(c) => { setEnableBenchReport(c as boolean); if (!c) setAllowBenchReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableBenchReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Access"
                        description="Define which bench data users can view"
                        control={
                          <Select value={viewBenchReportAccess} onValueChange={(v) => { setViewBenchReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting bench report data to Excel or CSV"
                        onToggle={() => { setAllowBenchReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowBenchReportExport} onCheckedChange={(c) => { setAllowBenchReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Audit Log Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-audit-log-report" className="cursor-pointer">Audit Log Report</Label>}
                    description="Access to audit log reports showing system activity and change history"
                    onToggle={() => { setEnableAuditLogReport(v => { if (v) setAllowAuditLogReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-audit-log-report"
                        checked={enableAuditLogReport}
                        onCheckedChange={(c) => { setEnableAuditLogReport(c as boolean); if (!c) setAllowAuditLogReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableAuditLogReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting audit log report data to Excel or CSV"
                        onToggle={() => { setAllowAuditLogReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowAuditLogReportExport} onCheckedChange={(c) => { setAllowAuditLogReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Management Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="management" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllManagement} onDeselectAll={handleDeselectAllManagement} />
          <Accordion type="multiple" defaultValue={['resource-directory', 'navigation-access', 'settings-access']} className="space-y-3">
            <SectionAccordionItem value="resource-directory" icon={<Users className="h-5 w-5" />} title="Resource Directory" description="Manage access to resource information and profiles">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="view-resource-directory" className="cursor-pointer">View Resource Directory</Label>}
                    description="View the resource directory and basic information"
                    onToggle={() => { setViewResourceDirectory(v => !v); setHasUnsavedChanges(true); }}
                    control={<Checkbox id="view-resource-directory" checked={viewResourceDirectory} onCheckedChange={(c) => { setViewResourceDirectory(c as boolean); setHasUnsavedChanges(true); }} />}
                  />
                  {viewResourceDirectory && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Resources"
                        description="Define which resource cards this role can see in the directory"
                        control={
                          <Select value={viewResourceDirectoryAccess} onValueChange={(v) => { setViewResourceDirectoryAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team (same department)</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                    </div>
                  )}
                </div>
                <PermissionCard
                  label={<Label htmlFor="view-resource-details" className="cursor-pointer">View Resource Details</Label>}
                  description="View detailed resource information and profiles"
                  onToggle={() => { setViewResourceDetails(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-resource-details" checked={viewResourceDetails} onCheckedChange={(c) => { setViewResourceDetails(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>

            {/* Navigation Access */}
            <SectionAccordionItem value="navigation-access" icon={<LayoutDashboard className="h-5 w-5" />} title="Navigation" description="Control which main navigation menu items this role can access">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-dashboard" className="cursor-pointer">View Dashboard</Label>}
                  description="Access the main Dashboard / Planner view"
                  onToggle={() => { setViewDashboard(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-dashboard"
                      checked={viewDashboard}
                      onCheckedChange={(c) => { setViewDashboard(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-heatmap" className="cursor-pointer">View Heatmap</Label>}
                  description="Access the Heatmap capacity view"
                  onToggle={() => { setViewHeatmap(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-heatmap"
                      checked={viewHeatmap}
                      onCheckedChange={(c) => { setViewHeatmap(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-open-demand" className="cursor-pointer">View Open Demand</Label>}
                  description="Access the Open Demand / Unallocated view"
                  onToggle={() => { setViewOpenDemand(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-open-demand"
                      checked={viewOpenDemand}
                      onCheckedChange={(c) => { setViewOpenDemand(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-projects" className="cursor-pointer">View Projects</Label>}
                  description="Access the Projects list view"
                  onToggle={() => { setViewProjects(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-projects"
                      checked={viewProjects}
                      onCheckedChange={(c) => { setViewProjects(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
              </div>
            </SectionAccordionItem>

            {/* Settings Access */}
            <SectionAccordionItem value="settings-access" icon={<Settings2 className="h-5 w-5" />} title="Settings" description="Control which settings menu items this role can access">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-general-settings" className="cursor-pointer">View General</Label>}
                  description="Access the General settings page"
                  onToggle={() => { setViewGeneralSettings(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-general-settings"
                      checked={viewGeneralSettings}
                      onCheckedChange={(c) => { setViewGeneralSettings(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-timezone-settings" className="cursor-pointer">View Timezone</Label>}
                  description="Access the Timezone settings page"
                  onToggle={() => { setViewTimezoneSettings(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-timezone-settings"
                      checked={viewTimezoneSettings}
                      onCheckedChange={(c) => { setViewTimezoneSettings(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-booking-type-setup" className="cursor-pointer">View Booking Type Setup</Label>}
                  description="Access the Booking Type Setup settings page"
                  onToggle={() => { setViewBookingTypeSetup(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-booking-type-setup"
                      checked={viewBookingTypeSetup}
                      onCheckedChange={(c) => { setViewBookingTypeSetup(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-group-setup" className="cursor-pointer">View Planner Group Setup</Label>}
                  description="Access the Planner Group Setup settings page"
                  onToggle={() => { setViewGroupSetup(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-group-setup"
                      checked={viewGroupSetup}
                      onCheckedChange={(c) => { setViewGroupSetup(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={
                    <Label htmlFor="view-roles-permissions" className={isAdministratorRole ? 'flex items-center gap-1.5' : 'cursor-pointer'}>
                      View Roles & Permissions
                      {isAdministratorRole && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </Label>
                  }
                  description={isAdministratorRole
                    ? 'Always enabled for the Administrator role and cannot be changed'
                    : 'Access the Roles & Permissions settings page'}
                  onToggle={isAdministratorRole ? undefined : () => { setViewRolesPermissions(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-roles-permissions"
                      checked={isAdministratorRole ? true : viewRolesPermissions}
                      disabled={isAdministratorRole}
                      onCheckedChange={(c) => { setViewRolesPermissions(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-user-setup" className="cursor-pointer">View User Setup</Label>}
                  description="Access the User Setup settings page"
                  onToggle={() => { setViewUserSetup(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-user-setup"
                      checked={viewUserSetup}
                      onCheckedChange={(c) => { setViewUserSetup(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
              </div>
            </SectionAccordionItem>

            {/* Booking Permissions */}
            <SectionAccordionItem value="booking-access" icon={<CalendarClock className="h-5 w-5" />} title="Bookings" description="Control who can create, edit, and delete bookings">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="create-booking" className="cursor-pointer">Create Bookings</Label>}
                  description="Create new bookings on the scheduler"
                  onToggle={() => { setCreateBooking(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="create-booking" checked={createBooking} onCheckedChange={(c) => { setCreateBooking(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="edit-booking" className="cursor-pointer">Edit Bookings</Label>}
                  description="Modify existing bookings, including status and approval changes"
                  onToggle={() => { setEditBooking(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="edit-booking" checked={editBooking} onCheckedChange={(c) => { setEditBooking(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="delete-booking" className="cursor-pointer">Delete Bookings</Label>}
                  description="Soft-delete or permanently remove bookings"
                  onToggle={() => { setDeleteBooking(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="delete-booking" checked={deleteBooking} onCheckedChange={(c) => { setDeleteBooking(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="override-read-only" className="cursor-pointer">Override Read-Only Bookings</Label>}
                  description="Edit bookings whose booking type is read-only for the user"
                  onToggle={() => { setOverrideReadOnly(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="override-read-only" checked={overrideReadOnly} onCheckedChange={(c) => { setOverrideReadOnly(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Data & Integration Tab ──────────────────────────────────────────── */}
        <TabsContent value="data" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllDataIntegration} onDeselectAll={handleDeselectAllDataIntegration} />
          <Accordion type="multiple" defaultValue={['integrations']} className="space-y-3">

            <SectionAccordionItem value="integrations" icon={<Plug className="h-5 w-5" />} title="Integrations" description="Manage access to system integrations and data synchronisation">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-integrations" className="cursor-pointer">View Integrations</Label>}
                  description="View integration configurations and status"
                  onToggle={manageIntegrations ? undefined : () => { setViewIntegrations(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-integrations" checked={viewIntegrations} disabled={manageIntegrations} onCheckedChange={(c) => { setViewIntegrations(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="manage-integrations" className="cursor-pointer">Manage Integrations</Label>}
                  description="Configure, connect, and disconnect system integrations"
                  onToggle={() => { setManageIntegrations(v => { const next = !v; if (next) setViewIntegrations(true); return next; }); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="manage-integrations" checked={manageIntegrations} onCheckedChange={(c) => { const next = c as boolean; setManageIntegrations(next); if (next) setViewIntegrations(true); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Users Tab ───────────────────────────────────────────────────────── */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex items-start gap-3 pb-4 border-b">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Users Assigned to {roleName}</h3>
              <p className="text-sm text-muted-foreground mt-1">Manage users assigned to this role and transfer them to different roles</p>
            </div>
            {!isCreateMode && (
              <Button onClick={handleOpenAssignDialog} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Assign User
              </Button>
            )}
          </div>
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-12 border rounded-lg bg-muted/20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading users...</span>
            </div>
          ) : users.length === 0 && !userSearchQuery.trim() ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
              <div className="text-center space-y-2">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No Users Assigned</p>
                <p className="text-sm text-muted-foreground">There are currently no users assigned to this role</p>
                {!isCreateMode && (
                  <Button variant="outline" onClick={handleOpenAssignDialog} className="mt-4">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign User
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {userSearchQuery.trim()
                    ? `${usersTotal} ${usersTotal === 1 ? 'user' : 'users'} match your search`
                    : `${usersTotal} ${usersTotal === 1 ? 'user' : 'users'} assigned to this role`}
                </p>
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search by name, email or role..."
                    value={userSearchQuery}
                    onChange={(e) => { setUserSearchQuery(e.target.value); setUserPage(1); }}
                    className="pl-9"
                  />
                </div>
              </div>
              {users.length === 0 ? (
                <div className="flex items-center justify-center py-12 border rounded-lg bg-muted/20">
                  <p className="text-sm text-muted-foreground">No users match your search</p>
                </div>
              ) : (
                <div className="border rounded-lg divide-y">
                  {users.map((user) => (
                    <div key={user.id} className="p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user.photoUrl} alt={user.name} />
                            <AvatarFallback className="text-xs">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span>{user.email}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                            {user.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedUser(user); setTransferDialogOpen(true); }}
                          >
                            <ArrowRight className="h-4 w-4 mr-1" />
                            Transfer
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <TablePagination
                page={userPage}
                pageSize={userPageSize}
                totalItems={usersTotal}
                onPageChange={setUserPage}
                onPageSizeChange={(size) => { setUserPageSize(size); setUserPage(1); }}
                itemLabel="users"
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Role Changes</DialogTitle>
            <DialogDescription>Review the role configuration before saving</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Role:</span>
                <span className="text-sm">{roleName}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Users affected:</span>
                <span className="text-sm">{usersTotal} {usersTotal === 1 ? 'user' : 'users'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Total permissions enabled:</span>
                <span className="text-sm font-semibold text-primary">{getTotalPermissionsCount()}</span>
              </div>
            </div>
            {(permissionChanges.added.length > 0 || permissionChanges.removed.length > 0) && (
              <>
                <Separator />
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {permissionChanges.added.map((label) => (
                    <div key={`add-${label}`} className="flex items-center gap-2 text-sm text-primary">
                      <Plus className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </div>
                  ))}
                  {permissionChanges.removed.map((label) => (
                    <div key={`rm-${label}`} className="flex items-center gap-2 text-sm text-destructive">
                      <Minus className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <Separator />
            <div className="rounded-lg bg-muted/50 p-3 border">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  These changes will be applied to all {usersTotal} {usersTotal === 1 ? 'user' : 'users'} with this role.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveConfirmation(false)}>Cancel</Button>
            <Button onClick={performSave} disabled={isSaving}>
              {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Confirm & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Role Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer User to Different Role</DialogTitle>
            <DialogDescription>Select a new role for {selectedUser?.name}. This will immediately update their permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Role</Label>
              <div className="p-3 rounded-lg border bg-muted/50"><p className="font-medium">{roleName}</p></div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role">New Role</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger id="new-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>
                  {allRoles.filter(role => String(role.id) !== String(roleId)).map(role => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      <div className="flex items-center gap-2">
                        <span>{role.name}</span>
                        {role.isSystem && <Badge variant="secondary" className="text-xs">Platform</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedUser && (
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-sm text-muted-foreground">
                  <strong>{selectedUser.name}</strong> will be transferred from <strong>{roleName}</strong> to the selected role.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmTransfer} disabled={!newRoleId}>Transfer User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Assign User to {roleName}</DialogTitle>
            <DialogDescription>Select a resource to assign to this role</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={assignSearchQuery}
              onChange={(e) => { setAssignSearchQuery(e.target.value); setAssignPage(1); }}
              className="pl-9"
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-[400px] max-h-[400px] border rounded-lg divide-y">
            {isLoadingResources ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading resources...</span>
              </div>
            ) : availableResources.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-muted-foreground">
                  {assignSearchQuery ? 'No matching resources found' : 'No available resources to assign'}
                </p>
              </div>
            ) : (
              availableResources.map((resource) => (
                <div key={resource.id} className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                      <AvatarFallback className="text-xs">
                        {getInitials(resource.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{resource.name}</p>
                      {resource.email && (
                        <p className="text-xs text-muted-foreground">{resource.email}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isAssigning}
                    onClick={() => handleAssignUser(resource)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Assign
                  </Button>
                </div>
              ))
            )}
          </div>
          <TablePagination
            page={assignPage}
            pageSize={assignPageSize}
            totalItems={assignPagination?.totalItems ?? 0}
            onPageChange={setAssignPage}
            onPageSizeChange={(size) => { setAssignPageSize(size); setAssignPage(1); }}
            itemLabel="resources"
            alwaysShow
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 right-0 z-20 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[left] duration-200 ease-linear" style={{ left: sidebarLeft }}>
        <div className="flex h-16 items-center justify-between gap-4 px-6 max-w-[2000px] mx-auto">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="lg" className="min-w-[120px]" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
            <Button size="lg" className="min-w-[120px]" onClick={handleSave} disabled={(isCreateMode ? false : !hasUnsavedChanges) || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                isCreateMode ? 'Create Role' : 'Save Settings'
              )}
            </Button>
          </div>
        </div>
      </div>

      <DiscardChangesDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onConfirm={handleConfirmDiscard}
      />
    </div>
  );
};
